/**
 * LinkedIn data takeout ZIP parser. User exports via Settings -> Data
 * Privacy -> Get a copy of your data. Receives a zip with CSVs:
 *   Connections.csv, Positions.csv, Education.csv, Profile.csv, etc.
 *
 * Dial uses this to seed work-circle tagging + reciprocity baseline.
 * Tankhwah v2 may use Positions.csv to cohort-compare salary trajectory.
 */
import type { ContactSeed, WorkProfile, Provenance } from './types.ts';
import { MOCK_CONTACTS, MOCK_WORK } from './mock-data.ts';

export interface LinkedInImport {
  readonly contacts: readonly ContactSeed[];
  readonly work: readonly WorkProfile[];
}

export async function parseLinkedInZip(input: { mode: 'mock' } | { mode: 'file'; file: Blob }): Promise<LinkedInImport> {
  if (input.mode === 'mock') return { contacts: MOCK_CONTACTS, work: MOCK_WORK };

  const buf = await input.file.arrayBuffer();
  const entries = await unzipCsvEntries(buf);
  const connectionsRaw = entries.get('Connections.csv') ?? '';
  const positionsRaw = entries.get('Positions.csv') ?? '';

  const contacts = parseConnections(connectionsRaw);
  const work = parsePositions(positionsRaw);
  return { contacts, work };
}

function parseConnections(csv: string): readonly ContactSeed[] {
  const rows = parseCsv(csv);
  if (rows.length === 0) return [];
  const header = rows[0]!.map((c) => c.toLowerCase());
  const firstIdx = header.indexOf('first name');
  const lastIdx = header.indexOf('last name');
  const emailIdx = header.indexOf('email address');
  const companyIdx = header.indexOf('company');
  const positionIdx = header.indexOf('position');
  const urlIdx = header.indexOf('url');
  const out: ContactSeed[] = [];
  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r] ?? [];
    const name = `${row[firstIdx] ?? ''} ${row[lastIdx] ?? ''}`.trim();
    if (!name) continue;
    const email = (row[emailIdx] ?? '').trim();
    const provenance: Provenance = { connector: 'linkedin', source_id: row[urlIdx] ?? name, fetched_at: Date.now() };
    const isWork = Boolean(row[companyIdx] || row[positionIdx]);
    out.push({
      external_id: row[urlIdx] ?? `li-${r}`,
      name,
      phones: [],
      emails: email ? [email] : [],
      ...(isWork ? { relation_hint: 'work' } : {}),
      source: 'linkedin',
      provenance,
    });
  }
  return out;
}

function parsePositions(csv: string): readonly WorkProfile[] {
  const rows = parseCsv(csv);
  if (rows.length === 0) return [];
  const header = rows[0]!.map((c) => c.toLowerCase());
  const companyIdx = header.indexOf('company name');
  const titleIdx = header.indexOf('title');
  const startIdx = header.indexOf('started on');
  const endIdx = header.indexOf('finished on');
  const locIdx = header.indexOf('location');
  const out: WorkProfile[] = [];
  for (let r = 1; r < rows.length; r += 1) {
    const row = rows[r] ?? [];
    const company = (row[companyIdx] ?? '').trim();
    if (!company) continue;
    const title = (row[titleIdx] ?? '').trim();
    const provenance: Provenance = { connector: 'linkedin', source_id: `${company}|${title}`, fetched_at: Date.now() };
    out.push({
      person_key: '__self__',
      company,
      title,
      start: row[startIdx] ?? '',
      ...(row[endIdx] ? { end: row[endIdx] } : {}),
      ...(row[locIdx] ? { location: row[locIdx] } : {}),
      source: 'linkedin',
      provenance,
    });
  }
  return out;
}

function parseCsv(csv: string): string[][] {
  const out: string[][] = [];
  let cur: string[] = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < csv.length; i += 1) {
    const c = csv[i];
    if (inQuotes) {
      if (c === '"' && csv[i + 1] === '"') { field += '"'; i += 1; }
      else if (c === '"') inQuotes = false;
      else field += c;
    } else if (c === '"') inQuotes = true;
    else if (c === ',') { cur.push(field); field = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && csv[i + 1] === '\n') i += 1;
      cur.push(field);
      if (cur.some((x) => x.length > 0)) out.push(cur);
      cur = [];
      field = '';
    } else field += c ?? '';
  }
  if (field.length > 0 || cur.length > 0) {
    cur.push(field);
    if (cur.some((x) => x.length > 0)) out.push(cur);
  }
  return out;
}

async function unzipCsvEntries(buf: ArrayBuffer): Promise<Map<string, string>> {
  const view = new DataView(buf);
  const eocdMaxScan = Math.min(buf.byteLength, 65_557);
  let eocdOffset = -1;
  for (let i = buf.byteLength - 22; i >= buf.byteLength - eocdMaxScan; i -= 1) {
    if (view.getUint32(i, true) === 0x06054b50) { eocdOffset = i; break; }
  }
  const out = new Map<string, string>();
  if (eocdOffset < 0) return out;
  const cdSize = view.getUint32(eocdOffset + 12, true);
  const cdOffset = view.getUint32(eocdOffset + 16, true);
  const bytes = new Uint8Array(buf);
  let p = cdOffset;
  while (p < cdOffset + cdSize) {
    if (view.getUint32(p, true) !== 0x02014b50) break;
    const method = view.getUint16(p + 10, true);
    const compSize = view.getUint32(p + 20, true);
    const fnameLen = view.getUint16(p + 28, true);
    const extraLen = view.getUint16(p + 30, true);
    const commentLen = view.getUint16(p + 32, true);
    const localHeaderOffset = view.getUint32(p + 42, true);
    const filename = new TextDecoder().decode(bytes.subarray(p + 46, p + 46 + fnameLen));
    p += 46 + fnameLen + extraLen + commentLen;
    if (!/\.csv$/i.test(filename)) continue;
    const lhFnameLen = view.getUint16(localHeaderOffset + 26, true);
    const lhExtraLen = view.getUint16(localHeaderOffset + 28, true);
    const dataStart = localHeaderOffset + 30 + lhFnameLen + lhExtraLen;
    const raw = bytes.subarray(dataStart, dataStart + compSize);
    let text = '';
    if (method === 0) text = new TextDecoder().decode(raw);
    else if (method === 8) {
      const stream = new Blob([raw]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
      text = await new Response(stream).text();
    }
    const baseName = filename.split('/').pop() ?? filename;
    out.set(baseName, text);
  }
  return out;
}
